import { useEffect, useMemo, useState } from "react"
import Icon from "../../Icon"
import api from "../../../utils/api"

const SECTION_META = {
  bulkInvoice: {
    icon: "chat-square-text",
    title: "Bulk Invoice SMS",
    description: "Customize the SMS sent after bulk tenant invoicing.",
    toggleLabel: "Send SMS after invoicing",
    saveLabel: "Save Invoice SMS",
  },
  paymentReceipt: {
    icon: "receipt",
    title: "Payment Receipt SMS",
    description: "Customize the SMS sent immediately after a successful payment.",
    toggleLabel: "Send SMS after payment",
    saveLabel: "Save Payment SMS",
  },
  chamaaPayout: {
    icon: "cash-stack",
    title: "Chamaa Payout SMS",
    description: "Customize the payout confirmation SMS for Chamaa disbursements.",
    toggleLabel: "Send SMS after Chamaa payout",
    saveLabel: "Save Chamaa SMS",
  },
  welfarePayout: {
    icon: "heart",
    title: "Welfare Payout SMS",
    description: "Customize the payout confirmation SMS for Welfare disbursements.",
    toggleLabel: "Send SMS after Welfare payout",
    saveLabel: "Save Welfare SMS",
  },
  frpDisbursement: {
    icon: "bank",
    title: "FRP Disbursement SMS",
    description: "Customize the payout confirmation SMS for FRP wallet disbursements.",
    toggleLabel: "Send SMS after FRP disbursement",
    saveLabel: "Save FRP SMS",
  },
  adminTopup: {
    icon: "wallet2",
    title: "Admin Top Up SMS",
    description: "Customize the SMS sent when Super Admin funds a user wallet.",
    toggleLabel: "Send SMS after admin top up",
    saveLabel: "Save Top Up SMS",
  },
}
const DATE_PLACEHOLDER_TOKEN = "{{date}}"
const DATE_PLACEHOLDER_EXAMPLE = "12/Jan/2026"

function AdminBulkInvoiceSmsSettingsPanel({ defaultCollapsed = true }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [loading, setLoading] = useState(false)
  const [savingKey, setSavingKey] = useState("")
  const [sections, setSections] = useState([])

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true)
        const res = await api.get("/frp/admin/bulk-invoice-notification-settings")
        const settings = res?.data?.settings || {}
        const smsDefinitions = Array.isArray(res?.data?.smsDefinitions) ? res.data.smsDefinitions : []
        setSections(
          smsDefinitions.map((definition) => ({
            ...definition,
            enabled: settings?.[definition.enabledKey] !== false,
            template: settings?.[definition.templateKey] || "",
            open: definition.key === "bulkInvoice" || definition.key === "paymentReceipt",
          }))
        )
      } catch (error) {
        console.error("Failed to fetch SMS notification settings", error)
        alert(error?.response?.data?.error || "Failed to load SMS settings")
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [])

  const sectionOrder = useMemo(
    () => ["bulkInvoice", "paymentReceipt", "chamaaPayout", "welfarePayout", "frpDisbursement", "adminTopup"],
    []
  )

  const orderedSections = useMemo(() => {
    const orderMap = new Map(sectionOrder.map((key, index) => [key, index]))
    return [...sections].sort((a, b) => (orderMap.get(a.key) ?? 999) - (orderMap.get(b.key) ?? 999))
  }, [sections, sectionOrder])

  const updateSection = (key, patch) => {
    setSections((prev) => prev.map((section) => (section.key === key ? { ...section, ...patch } : section)))
  }

  const insertPlaceholder = (key, token) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.key !== key) return section
        const base = String(section.template || "")
        return {
          ...section,
          template: `${base}${base.endsWith(" ") || base.length === 0 ? "" : " "}${token}`,
        }
      })
    )
  }

  const saveSection = async (section) => {
    try {
      setSavingKey(section.key)
      const payload = {
        [section.enabledKey]: section.enabled,
        [section.templateKey]: section.template,
      }
      const res = await api.put("/frp/admin/bulk-invoice-notification-settings", payload)
      const settings = res?.data?.settings || {}
      setSections((prev) =>
        prev.map((entry) =>
          entry.key === section.key
            ? {
                ...entry,
                enabled: settings?.[entry.enabledKey] !== false,
                template: settings?.[entry.templateKey] || entry.template,
              }
            : entry
        )
      )
      alert(res?.data?.message || `${SECTION_META[section.key]?.title || "SMS"} settings updated successfully`)
    } catch (error) {
      console.error("Failed to save SMS notification settings", error)
      alert(error?.response?.data?.error || "Failed to save SMS settings")
    } finally {
      setSavingKey("")
    }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-[#fafcfd] px-5 py-4 text-left"
      >
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-slate-800">
            <Icon name="chat-square-text" size="18px" />
            Super Admin SMS Settings
          </h2>
          <p className="text-xs text-slate-500">
            Configure confirmation messages for every invoicing, payment, and disbursement module.
          </p>
        </div>
        <Icon
          name={collapsed ? "chevron-right" : "chevron-down"}
          size="1rem"
          className="text-slate-400 transition-transform duration-200"
        />
      </button>

      {!collapsed && (
        <div className="space-y-4 p-5">
          {orderedSections.map((section) => {
            const meta = SECTION_META[section.key] || {}
            const isSaving = savingKey === section.key
            return (
              <div key={section.key} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <button
                  type="button"
                  onClick={() => updateSection(section.key, { open: !section.open })}
                  className="flex w-full items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-4 text-left"
                >
                  <div>
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <Icon name={meta.icon || "chat-square-text"} size="16px" />
                      {meta.title || section.label}
                    </h3>
                    <p className="text-xs text-slate-500">{meta.description || section.label}</p>
                  </div>
                  <Icon
                    name={section.open ? "chevron-down" : "chevron-right"}
                    size="1rem"
                    className="text-slate-400 transition-transform duration-200"
                  />
                </button>

                {section.open && (
                  <div className="space-y-4 p-4">
                    <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">{meta.title || section.label}</h4>
                        <p className="text-xs text-slate-600">
                          Keep this enabled to send confirmations, and make the wording clearly identify the module.
                        </p>
                      </div>
                      <label className="inline-flex items-center cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={section.enabled}
                          onChange={(e) => updateSection(section.key, { enabled: e.target.checked })}
                          disabled={loading || isSaving}
                        />
                        <div className="relative h-5 w-10 rounded-full bg-gray-200 transition-colors peer-checked:bg-emerald-600">
                          <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all peer-checked:left-5" />
                        </div>
                        <span className="ml-2 text-xs font-medium text-slate-700">
                          {meta.toggleLabel || "Send SMS"}
                        </span>
                      </label>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {(section.placeholders || []).map((token) => (
                        <button
                          key={token}
                          type="button"
                          onClick={() => insertPlaceholder(section.key, token)}
                          disabled={loading || isSaving}
                          className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                        >
                          {token}
                        </button>
                      ))}
                    </div>

                    <textarea
                      value={section.template}
                      onChange={(e) => updateSection(section.key, { template: e.target.value })}
                      rows={5}
                      disabled={loading || isSaving}
                      placeholder={`Write the SMS template for ${meta.title || section.label}.`}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-100"
                    />

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-[11px] text-slate-500">
                        Available placeholders: {(section.placeholders || []).join(", ")}
                        {(section.placeholders || []).includes(DATE_PLACEHOLDER_TOKEN)
                          ? ` (${DATE_PLACEHOLDER_TOKEN} format: ${DATE_PLACEHOLDER_EXAMPLE})`
                          : ""}
                      </p>
                      <button
                        type="button"
                        onClick={() => saveSection(section)}
                        disabled={loading || isSaving}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSaving ? "Saving..." : meta.saveLabel || "Save SMS"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default AdminBulkInvoiceSmsSettingsPanel
